/**
 * Instructor voice layer (Beta v1).
 *
 * Source of truth for the 9 beta voice moments approved in the
 * "Audit HeirWise Modules for Instructor Voice Support" report.
 * The Lunimar Collective lore is the world name — never "Luminara".
 */

export type InstructorId = "aelyra" | "caelion" | "vaelor" | "lumi";

export type VoiceTrigger =
  | "module_intro"
  | "pre_decision"
  | "post_decision_success"
  | "post_decision_failure"
  | "hint"
  | "consequence"
  | "completion"
  | "reinforcement"
  | "partial_success";

export type VoiceModuleId =
  | "credit_management"
  | "scam_defense"
  | "budget_builder";

export interface InstructorVoiceMoment {
  moduleId: VoiceModuleId;
  momentId: string;
  instructor: InstructorId;
  trigger: VoiceTrigger;
  priority: "high" | "medium" | "low";
  /**
   * Beta v2 density tier. `essential` and `supportive` lines are wired
   * into the modules; `optional` lines are kept in the allowlist so they
   * can be generated and wired later without a schema change.
   */
  tier?: "essential" | "supportive" | "optional";
  transcript: string;
  estimatedDurationSeconds: number;
  conceptTags: string[];
}

export const INSTRUCTOR_LABELS: Record<InstructorId, string> = {
  aelyra: "Aelyra Solari",
  caelion: "Caelion Veyr",
  vaelor: "Headmaster Vaelor",
  lumi: "Lumi Vanta",
};

export const INSTRUCTOR_VOICE_MOMENTS: InstructorVoiceMoment[] = [
  // ─── Credit Management ────────────────────────────────────────────
  {
    moduleId: "credit_management",
    momentId: "credit.intro",
    instructor: "aelyra",
    trigger: "module_intro",
    priority: "high",
    tier: "essential",
    transcript:
      "Credit is not just a number. It's a signal of how consistently you keep your financial promises over time. Let's see what your choices reveal.",
    estimatedDurationSeconds: 9,
    conceptTags: ["credit_score", "patterns_over_time", "borrowing"],
  },
  {
    moduleId: "credit_management",
    momentId: "credit.postDecision.missed",
    instructor: "aelyra",
    trigger: "post_decision_failure",
    priority: "high",
    tier: "essential",
    transcript:
      "A missed payment doesn't just cost a fee. It rewrites the story your credit tells lenders for years.",
    estimatedDurationSeconds: 7,
    conceptTags: ["payment_history", "borrowing", "long_term_cost"],
  },
  {
    moduleId: "credit_management",
    momentId: "credit.completion",
    instructor: "aelyra",
    trigger: "completion",
    priority: "high",
    tier: "essential",
    transcript:
      "Your score reflects months of small choices. Strong credit isn't built in one move — it's built by showing up the same way, again and again.",
    estimatedDurationSeconds: 9,
    conceptTags: ["patterns_over_time", "financial_resilience"],
  },
  {
    moduleId: "credit_management",
    momentId: "credit.prePaymentChoice",
    instructor: "caelion",
    trigger: "pre_decision",
    priority: "high",
    tier: "essential",
    transcript:
      "The easiest choice today is not always the cheapest choice tomorrow. Look past the button and follow the obligation.",
    estimatedDurationSeconds: 8,
    conceptTags: ["future_obligation", "tradeoff"],
  },
  {
    moduleId: "credit_management",
    momentId: "credit.postDecision.paidFull",
    instructor: "aelyra",
    trigger: "post_decision_success",
    priority: "medium",
    tier: "supportive",
    transcript:
      "Strong choice. Paying on time protects the story your credit tells before anyone else gets to interpret it.",
    estimatedDurationSeconds: 8,
    conceptTags: ["payment_history", "credit_score"],
  },
  {
    moduleId: "credit_management",
    momentId: "credit.postDecision.minimumPaid",
    instructor: "aelyra",
    trigger: "post_decision_failure",
    priority: "high",
    tier: "essential",
    transcript:
      "Minimum payments can keep you current, but they can also keep you trapped. The balance does not disappear — it waits.",
    estimatedDurationSeconds: 9,
    conceptTags: ["interest", "carried_balance"],
  },
  {
    moduleId: "credit_management",
    momentId: "credit.warning.utilizationHigh",
    instructor: "lumi",
    trigger: "hint",
    priority: "medium",
    tier: "supportive",
    transcript:
      "Your available credit is getting crowded. Lenders often read that as pressure, even if you haven't missed a payment.",
    estimatedDurationSeconds: 8,
    conceptTags: ["utilization", "credit_score"],
  },
  {
    moduleId: "credit_management",
    momentId: "credit.consequence.scoreDrop",
    instructor: "caelion",
    trigger: "consequence",
    priority: "high",
    tier: "essential",
    transcript:
      "The number changed because the pattern changed. Credit systems respond less to intention and more to evidence.",
    estimatedDurationSeconds: 9,
    conceptTags: ["credit_score", "pattern_recognition"],
  },
  {
    moduleId: "credit_management",
    momentId: "credit.preQuizReview",
    instructor: "aelyra",
    trigger: "pre_decision",
    priority: "medium",
    tier: "supportive",
    transcript:
      "Before the check, remember the pattern: pay on time, keep balances controlled, and understand the cost before you borrow.",
    estimatedDurationSeconds: 9,
    conceptTags: ["reflection", "review"],
  },

  // ─── Scam Defense ─────────────────────────────────────────────────
  {
    moduleId: "scam_defense",
    momentId: "scam.intro",
    instructor: "caelion",
    trigger: "module_intro",
    priority: "high",
    tier: "essential",
    transcript:
      "Scams don't beat you with information. They beat you with speed. Today we slow them down.",
    estimatedDurationSeconds: 7,
    conceptTags: ["urgency", "behavioral_bias", "pattern_recognition"],
  },
  {
    moduleId: "scam_defense",
    momentId: "scam.postDecision.fail",
    instructor: "aelyra",
    trigger: "post_decision_failure",
    priority: "high",
    tier: "essential",
    transcript:
      "That message used urgency to bypass your thinking. In real life, that is exactly how good people lose real money.",
    estimatedDurationSeconds: 8,
    conceptTags: ["urgency_red_flag", "behavioral_bias"],
  },
  {
    moduleId: "scam_defense",
    momentId: "scam.postDecision.win",
    instructor: "caelion",
    trigger: "post_decision_success",
    priority: "high",
    tier: "essential",
    transcript:
      "Strong read. You didn't just refuse — you recognized the pattern. That is the skill that protects you forever.",
    estimatedDurationSeconds: 8,
    conceptTags: ["pattern_recognition", "scam_defense"],
  },
  {
    moduleId: "scam_defense",
    momentId: "scam.preFirstMessage",
    instructor: "lumi",
    trigger: "pre_decision",
    priority: "high",
    tier: "essential",
    transcript:
      "Before you react, pause. A scam usually tries to control your speed before it controls your money.",
    estimatedDurationSeconds: 8,
    conceptTags: ["urgency", "slow_down"],
  },
  {
    moduleId: "scam_defense",
    momentId: "scam.hint.suspiciousLink",
    instructor: "lumi",
    trigger: "hint",
    priority: "medium",
    tier: "supportive",
    transcript:
      "That link is wearing a costume. The words may look familiar, but the address is where the truth usually hides.",
    estimatedDurationSeconds: 8,
    conceptTags: ["link_verification", "verification_habit"],
  },
  {
    moduleId: "scam_defense",
    momentId: "scam.warning.fakeAuthority",
    instructor: "aelyra",
    trigger: "consequence",
    priority: "medium",
    tier: "supportive",
    transcript:
      "Real institutions do not need panic to prove who they are. Pressure from authority is often the signal to slow down.",
    estimatedDurationSeconds: 9,
    conceptTags: ["authority_impersonation", "behavioral_bias"],
  },
  {
    moduleId: "scam_defense",
    momentId: "scam.warning.urgency",
    instructor: "caelion",
    trigger: "reinforcement",
    priority: "high",
    tier: "essential",
    transcript:
      "Urgency is not proof that something matters. Sometimes it is the tool used to keep you from thinking clearly.",
    estimatedDurationSeconds: 9,
    conceptTags: ["urgency_manipulation", "behavioral_bias"],
  },
  {
    moduleId: "scam_defense",
    momentId: "scam.warning.tooGood",
    instructor: "caelion",
    trigger: "reinforcement",
    priority: "medium",
    tier: "supportive",
    transcript:
      "If the reward has no visible cost, the cost may be hidden somewhere you cannot see yet.",
    estimatedDurationSeconds: 7,
    conceptTags: ["too_good_to_be_true", "hidden_cost"],
  },
  {
    moduleId: "scam_defense",
    momentId: "scam.partial.blockedButUnclear",
    instructor: "aelyra",
    trigger: "partial_success",
    priority: "high",
    tier: "essential",
    transcript:
      "Blocking helps, but understanding protects you next time. The real win is naming the pattern before it names you.",
    estimatedDurationSeconds: 9,
    conceptTags: ["pattern_recognition", "red_flag"],
  },
  {
    moduleId: "scam_defense",
    momentId: "scam.success.redFlagIdentified",
    instructor: "caelion",
    trigger: "post_decision_success",
    priority: "medium",
    tier: "supportive",
    transcript:
      "That is the skill. You weren't guessing — you were reading the intent behind the message.",
    estimatedDurationSeconds: 7,
    conceptTags: ["pattern_recognition", "scam_defense"],
  },

  // ─── Budget Builder / Sim Wallet ──────────────────────────────────
  {
    moduleId: "budget_builder",
    momentId: "budget.intro",
    instructor: "aelyra",
    trigger: "module_intro",
    priority: "high",
    tier: "essential",
    transcript:
      "A budget isn't a restriction. It's a plan that tells your money where to go before pressure decides for you.",
    estimatedDurationSeconds: 8,
    conceptTags: ["budgeting", "needs_vs_wants", "control"],
  },
  {
    moduleId: "budget_builder",
    momentId: "budget.hint.noEmergency",
    instructor: "lumi",
    trigger: "hint",
    priority: "high",
    tier: "essential",
    transcript:
      "No emergency cushion yet? Let's pause. The first surprise expense will land somewhere — better in savings than in debt.",
    estimatedDurationSeconds: 9,
    conceptTags: ["emergency_fund", "financial_resilience"],
  },
  {
    moduleId: "budget_builder",
    momentId: "budget.completion",
    instructor: "aelyra",
    trigger: "completion",
    priority: "high",
    tier: "essential",
    transcript:
      "You just rehearsed something most adults never practice — making the money decision before the pressure arrives.",
    estimatedDurationSeconds: 9,
    conceptTags: ["budgeting", "delayed_gratification"],
  },
  {
    moduleId: "budget_builder",
    momentId: "budget.postIncomeSelected",
    instructor: "aelyra",
    trigger: "pre_decision",
    priority: "medium",
    tier: "supportive",
    transcript:
      "Income starts the plan, but it does not finish it. What matters now is how much of that money is already spoken for before you spend.",
    estimatedDurationSeconds: 10,
    conceptTags: ["cash_flow", "budgeting"],
  },
  {
    moduleId: "budget_builder",
    momentId: "budget.deductionsExplained",
    instructor: "aelyra",
    trigger: "consequence",
    priority: "high",
    tier: "essential",
    transcript:
      "This is the first lesson of real cash flow. What you earn and what reaches your account are not always the same number.",
    estimatedDurationSeconds: 9,
    conceptTags: ["take_home_pay", "cash_flow"],
  },
  {
    moduleId: "budget_builder",
    momentId: "budget.preAllocation",
    instructor: "caelion",
    trigger: "pre_decision",
    priority: "medium",
    tier: "supportive",
    transcript:
      "Every dollar you place here disappears from another possibility. That is not punishment — that is tradeoff.",
    estimatedDurationSeconds: 8,
    conceptTags: ["opportunity_cost", "tradeoff"],
  },
  {
    moduleId: "budget_builder",
    momentId: "budget.consequence.surpriseExpense",
    instructor: "caelion",
    trigger: "consequence",
    priority: "high",
    tier: "essential",
    transcript:
      "This is where earlier choices speak. A surprise expense does not ask whether you are ready — it only reveals whether you prepared.",
    estimatedDurationSeconds: 10,
    conceptTags: ["financial_resilience", "preparation"],
  },
  {
    moduleId: "budget_builder",
    momentId: "budget.success.strongBudget",
    instructor: "aelyra",
    trigger: "completion",
    priority: "medium",
    tier: "supportive",
    transcript:
      "You built more than a budget. You built breathing room — and breathing room is what keeps pressure from becoming panic.",
    estimatedDurationSeconds: 9,
    conceptTags: ["financial_resilience", "budgeting"],
  },
];

export const INSTRUCTOR_VOICE_MAP: Record<string, InstructorVoiceMoment> =
  Object.fromEntries(INSTRUCTOR_VOICE_MOMENTS.map((m) => [m.momentId, m]));

export function getMoment(momentId: string): InstructorVoiceMoment | undefined {
  return INSTRUCTOR_VOICE_MAP[momentId];
}